/**
 * Copyright Mántum All Rights Reserved.
 */
import debug from 'debug';
import { useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
import { validator } from '@mantum/kanela';

import {
    ACTION_DATASOURCE,
    EVENT_CHANGED,
    useGroupDispatch
} from '../../group';

import type { Filter, Detail } from '@mantum/kanela';

const info = debug('kanela:image');

const rProperty = /\{[a-z]+\}/i;

type PropTypes = {
    advanced?: {
        filter?: Filter;
        origin?: string;
    };
    onChange?: (name?: string, value?: unknown) => void;
    dataSource?: string;
    disabled?: boolean;
    name?: string;
    testid?: string;
};

export default forwardRef<Detail<unknown>, PropTypes>(
    function Image(props, ref) {
        // prettier-ignore
        const {
            advanced = {}
        } = props;

        // prettier-ignore
        const {
            origin = ''
        } = advanced;

        const changeRef = useRef(props.onChange);
        const inputRef = useRef<HTMLObjectElement>(null);

        const dispatch = useGroupDispatch();

        useImperativeHandle(
            ref,
            () => {
                return {
                    name: props.name,
                    validable: false,
                    clear() {
                        if (props.name && origin) {
                            inputRef.current?.contentWindow?.postMessage(
                                props.name,
                                origin
                            );
                        }

                        if (changeRef.current) {
                            changeRef.current(props.name, null);
                        }
                    },
                    validate() {
                        return true;
                    },
                    value() {
                        return null;
                    }
                };
            },
            [props.name, origin]
        );

        useEffect(() => {
            changeRef.current = props.onChange;
        }, [props.onChange]);

        useEffect(() => {
            const message = (event: MessageEvent) => {
                const filter = props.advanced?.filter;
                if (!filter || props.disabled) {
                    info('filter is not defined or component is disabled');
                    return;
                }

                if (!props.name || event.origin !== origin) {
                    info(
                        'name is not defined or %s !== %s',
                        event.origin,
                        origin
                    );
                    return;
                }

                const data = event.data;
                if (!data || !data.name || data.name !== props.name) {
                    info(
                        'name is not defined or %s !== %s',
                        data.name,
                        props.name
                    );
                    return;
                }

                const values = data.value;
                if (changeRef.current) {
                    changeRef.current(props.name, values);
                }

                if (!values) {
                    dispatch({
                        type: EVENT_CHANGED,
                        content: [
                            {
                                action: ACTION_DATASOURCE,
                                target: filter.target,
                                value: []
                            }
                        ]
                    });
                } else {
                    let dataSource = null;

                    if (
                        typeof filter.dataSource === 'string' &&
                        !validator.isDataSource(filter.dataSource)
                    ) {
                        const value = filter.dataSource
                            .split('.')
                            .filter((it) => it !== '')
                            .reduce(
                                (previous, current) =>
                                    previous[current as keyof unknown],
                                values
                            );

                        if (Array.isArray(value)) {
                            dataSource = value;
                        }
                    } else if (
                        typeof filter.dataSource === 'object' &&
                        validator.isDataSource(filter.dataSource)
                    ) {
                        const result: Record<string, unknown> = {};

                        let url = filter.dataSource.url;
                        const parameters = filter.dataSource?.parameter ?? {};

                        Object.keys(parameters).forEach((key) => {
                            const element = parameters[key] as string;

                            const property =
                                typeof element === 'string'
                                    ? element
                                          .split('.')
                                          .filter((it) => it !== '')
                                    : element;

                            const value =
                                typeof property === 'string' ||
                                Array.isArray(property)
                                    ? property.reduce((previous, current) => {
                                          return current in previous
                                              ? previous[
                                                    current as keyof unknown
                                                ]
                                              : property;
                                      }, values)
                                    : element;

                            result[key] = value;
                        });

                        if (rProperty.test(filter.dataSource.url)) {
                            Object.keys(values).forEach((key) => {
                                url = url.replace(
                                    `{${key}}`,
                                    values[key as keyof unknown]
                                );
                            });
                        }

                        dataSource = {
                            ...filter.dataSource,
                            url,
                            parameter: result
                        };
                    }

                    dispatch({
                        type: EVENT_CHANGED,
                        content: [
                            {
                                action: ACTION_DATASOURCE,
                                target: filter.target,
                                value: dataSource,
                                clear: true
                            }
                        ]
                    });
                }
            };

            window.addEventListener('message', message);
            return () => {
                window.removeEventListener('message', message);
            };
        }, [
            props.name,
            dispatch,
            props.advanced?.filter,
            props.disabled,
            origin
        ]);

        return (
            <object
                className='h-full w-full'
                data-testid={props.testid}
                data={props.dataSource}
                name={props.name}
                ref={inputRef}
                type='image/svg+xml'
            >
                this object has image
            </object>
        );
    }
);
